import { clearAuthCookies, getAuthToken } from "../Auth/authCookie";

// Несколько параллельных запросов могут словить 401 одновременно (страница
// обычно делает сразу несколько вызовов на монтировании). Без этого флага
// каждый из них независимо читал бы window.location.href и строил свой
// собственный redirect — второй вызов подхватывал бы URL, уже переписанный
// первым (с ?redirect=...), и кодировал его ещё раз поверх, превращая
// %3A в %253A. Флаг гарантирует, что реально сработает только первый вызов.
let isRedirectingToLogin = false;

// Общая точка входа для любого места в приложении, которое решает, что
// пользователя нужно отправить на логин с возвратом на текущую страницу.
// Гарантирует, что при нескольких одновременных 401 (или вызове из Layout
// параллельно с вызовом из AdvancedFetch) реально сработает только первый —
// иначе второй вызов подхватил бы уже переписанный window.location.href
// (с ?redirect=...) и закодировал бы его ещё раз поверх.
export function redirectToLoginWithReturn() {
    if (isRedirectingToLogin) return;
    isRedirectingToLogin = true;

    const basicUrl = window.location.protocol === 'https:' ? process.env.REACT_APP_BASIC : process.env.REACT_APP_LOCAL_BASIC;
    const currentUrl = window.location.href;
    const loginUrl = `${basicUrl}/login?redirect=${encodeURIComponent(currentUrl)}`;

    window.location.href = loginUrl;
}

function clearAuthCookieAndRedirectToLogin() {
    clearAuthCookies();

    redirectToLoginWithReturn();
}

const AUTH_BOOTSTRAP_PATHS = [
    '/auth/login',
    '/auth/sign-up',
    '/auth/available-to-login',
    '/forgot-password',
    '/phone-verification/',
    '/user/email-verify',
    '/user/email-resend',
];

function isAuthBootstrapRequest(url: string): boolean {
    return AUTH_BOOTSTRAP_PATHS.some((path) => url.includes(path));
}

export function AdvancedFetch(url: string, options?: RequestInit) {
    const token = getAuthToken();
    
    const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...((options?.headers as Record<string, string>) || {}),
    };

    return fetch(url, { 
        ...options, 
        headers
    })
        .then(async res => {
            if (res.status === 401) {
                if (!isAuthBootstrapRequest(url)) {
                    clearAuthCookieAndRedirectToLogin();
                }
                throw new Error('Unauthorized');
            }
            if (res.status === 404) {
                throw new Error('404 Page Not Found')
            }
            if (res.status === 422) {
                const message = await res.json().then((result) => {
                    const firstFieldError = result?.errors && Object.values(result.errors)[0]
                    return (Array.isArray(firstFieldError) ? firstFieldError[0] : null) || result.message
                })
                throw Object.assign(new Error(message), { status: 422 })
            }
            if (res.status >= 400) {
                let message = res.statusText || `HTTP ${res.status}`;
                if (res.status < 500) {
                    const body = await res.json().catch(() => null);
                    message = body?.message || message;
                } else {
                    message = 'Ошибка сервера. Попробуйте ещё раз позже.';
                }
                throw Object.assign(new Error(message), { status: res.status });
            }
            if (res.status === 204) {
              return null;
            }

            const contentType = res.headers.get("content-type");
            if (contentType?.startsWith('application/json')) {
                return res.json()
            }
            if (contentType?.startsWith('text/plain')) {
                return res.text()
            }
            if (contentType?.startsWith('text/html')) {
                return res.text()
            }

            return res
        })
}

export function AdvancedFetchWithTimeout(url: string, options?: RequestInit, timeoutMs = 30000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    return AdvancedFetch(url, { ...options, signal: controller.signal })
        .finally(() => clearTimeout(timer));
}

// Для обратной совместимости - содержит только метод и пустые headers
// Authorization и Content-Type теперь добавляются автоматически в AdvancedFetch
export const requestOptions = {
    method: 'GET',
    headers: {}
};
